import { useCallback, useEffect, useState } from 'react'
import { androidLocalRefresh } from 'src/@api/android'
import { getLocalAndroidAll } from 'src/@api/dashboard'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'

/**
 * 将接口返回的权限状态合并进静态模板，并按已开启项排序。
 * @param {Array} currentTemplate - buildPermissionTemplate 生成的模板
 * @param {Object} contents - 接口 PERMISSION_LIST contents
 * @returns {Array}
 */
const mergePermissionTemplate = (currentTemplate, contents) => {
  const contentsArray = Object.entries(contents || {})

  const updatedStaticData = currentTemplate.map(item => {
    const found = contentsArray.find(([key]) => key === item.data)
    if (found) {
      const [key, value] = found

      return { ...item, permissions: key, status: value === 1 }
    }

    return { ...item, status: false }
  })

  updatedStaticData.sort((a, b) => (b.status === true ? 1 : 0) - (a.status === true ? 1 : 0))

  return updatedStaticData
}

/**
 * 权限检查页数据：拉 PERMISSION_LIST、合并模板，设备切换时重拉。
 *
 * @param {Object} options
 * @param {Array} options.template - 当前 locale 的权限模板
 * @param {boolean} options.translationReady - i18n 是否就绪
 * @returns {{ staticData: Array, ischange: *, onChange: Function }}
 */
export function usePermissionData({ template, translationReady }) {
  const [staticData, setStaticData] = useState(template)
  const [ischange, setIschange] = useState()

  /** 刷新设备端权限并拉取列表，支持 AbortSignal 防竞态 */
  const loadPermissionData = useCallback(async (deviceUdid, currentTemplate, { signal, isCurrent } = {}) => {
    if (!deviceUdid || !currentTemplate?.length) return

    try {
      await androidLocalRefresh({ device_id: deviceUdid, type: '42' })
    } catch (e) {
      console.error(e)
    }

    if (isCurrent && !isCurrent()) return

    try {
      const result = await getLocalAndroidAll(
        {
          username: readUserEmail(),
          params: { category: 'PERMISSION_LIST', deviceUdid }
        },
        signal ? { signal } : undefined
      )

      if (isCurrent && !isCurrent()) return

      const updatedStaticData = mergePermissionTemplate(currentTemplate, result?.data?.contents)
      setStaticData(updatedStaticData)

      const allStatusOn = updatedStaticData.every(i => i.status === true)
      localStorage.setItem('allStatusOn', String(allStatusOn))
      setIschange(allStatusOn ? null : 0)
    } catch (err) {
      if (isCurrent && !isCurrent() || err?.code === 'ERR_CANCELED') return
      console.error('getData error:', err)
    }
  }, [])

  // locale 切换时同步模板结构（不触发设备切换逻辑）
  useEffect(() => {
    setStaticData(template)
  }, [template])

  // 设备切换时重置并重新拉取权限列表
  useDeviceScopedInit(
    useCallback(
      (_, { context, signal, isCurrent }) => {
        if (!translationReady) return

        const deviceUdid = readLocalDeviceUdid(context)
        if (!deviceUdid) return

        setStaticData(template)
        setIschange(undefined)
        loadPermissionData(deviceUdid, template, { signal, isCurrent })
      },
      [translationReady, template, loadPermissionData]
    )
  )

  // 仅 locale / 模板变化时重拉（同设备）
  useEffect(() => {
    if (!translationReady) return

    const deviceUdid = readLocalDeviceUdid()
    if (!deviceUdid) return

    setStaticData(template)
    loadPermissionData(deviceUdid, template, { isCurrent: () => true })
  }, [translationReady, template, loadPermissionData])

  const onChange = index => {
    setIschange(index)
  }

  return { staticData, ischange, onChange }
}
